import { useEffect, useState } from 'react'
import { Box, Divider, Stack } from '@mui/material_modern'
import FormModalHeading from 'components/generic/modals/form-modal/FormModalHeading'
import FormModalCustomActionBar from 'components/generic/modals/form-modal/FormModalCustomActionBar'
import {
  getActionButtons,
  getColumns,
} from 'components/containers/inbound-tickets/select-dispatch/helperSelectDispatch'
import { debounce, noop } from 'lodash'
import { useNavigate } from 'react-router-dom'

import BasicDataGridV2 from 'components/generic/datagrid/BasicDataGridV2'
import { useGetDispatchTicketsQuery } from 'services/dispatch'
import { useGetCustomersQuery } from 'services/customers'
import { dateFormatter, getISOLocaleMidnightTime } from 'utils/data-manipulation/valueFormatter'
import { DispatchTicketFilterBar } from 'components/containers/inbound-tickets/select-dispatch/DispatchTicketFilterBar.tsx'
import { addToDate } from 'utils/data-manipulation/common'
import AlertModal from 'components/generic/modals/dialog-modal/AlertModal'
import PropTypes from 'prop-types'
import useCreateIbtFromDt from 'components/containers/inbound-tickets/select-dispatch/useCreateIbtFromDt.ts'
import {
  ASSET_CATEGORY,
  DISPATCH_TICKET_STATUSES,
  DISPATCH_TICKET_TYPES,
  DISPATCH_TICKET_EXCLUDE_LINKED_FILTER,
  PAGE_SIZE,
} from 'utils/constants'
import { useAppSelector } from 'utils/custom-hooks/useTypedReduxStore.ts'
import { selectModalProps } from '../../../../state/ui/selectors.ts'
import { setSubmittedFromDispatchBlade } from 'state/inboundTicket'
import { useDispatch } from 'react-redux'
import { GridCallbackDetails, GridSelectionModel } from '@mui/x-data-grid-pro_modern'

interface CreateFromDispatchModalProps {
  ticketId: string
  billId: string
  ticketNumber: number
  customerId: string
  inboundTicketData: object
  materialTypesData: object
  formData: object
}

/**
 * This returns the list of names of CONTAINER assets attached to a dispatch ticket
 */
const getRelevantAssets = dispatchTicket => {
  const filteredAssetStatuses =
    dispatchTicket.dispatchTicketAssets?.filter(
      assetStatus => assetStatus.asset?.assetCategory === ASSET_CATEGORY.CONTAINER,
    ) || []
  return filteredAssetStatuses.map(assetStatus => assetStatus.asset?.assetName) || []
}

const CreateFromDispatchTicketModal = ({ onFormStateChange, onHideModal }) => {
  const dispatch = useDispatch()
  const [selectedDtArray, setSelectedDtArray] = useState([])
  const today = new Date()
  const { yardDetailsList, yardId } = useAppSelector(state => state.auth)
  const { inboundTicketData, materialTypesData, formData } =
    useAppSelector<CreateFromDispatchModalProps>(selectModalProps)

  const { enableIntercompanyDispatch } = useAppSelector(state => state.featureFlags.variables)

  const onSelectionModelChange = (selectionModel: GridSelectionModel, details: GridCallbackDetails<any>) => {
    setSelectedDtArray(selectionModel as any)
  }

  const currentlyLoggedInYardDetails = yardDetailsList?.find(yard => yard.id === yardId)

  const initialFilter = {
    search: '',
    scheduleDateFrom: getISOLocaleMidnightTime(addToDate(today.toISOString(), -7)),
    scheduleDateTo: getISOLocaleMidnightTime(addToDate(today.toISOString(), 1)),
    driver: '',
    customer: '',
    customerId: '',
    statuses: [DISPATCH_TICKET_STATUSES.COMPLETED.id, DISPATCH_TICKET_STATUSES.IN_PROGRESS.id],
    types: [
      DISPATCH_TICKET_TYPES.LIVE_LOAD.id,
      DISPATCH_TICKET_TYPES.EXCHANGE.id,
      DISPATCH_TICKET_TYPES.PICK_UP.id,
      DISPATCH_TICKET_TYPES.PICK_AND_RETURN.id,
    ],
    excludeLinkedTickets: DISPATCH_TICKET_EXCLUDE_LINKED_FILTER.IBT,
    excludeIntercompanyFromTypeFilter: enableIntercompanyDispatch,
    endYardLocationId: currentlyLoggedInYardDetails?.locationId,
  }
  const [filter, setFilter] = useState(initialFilter)
  const [debouncedFilter, setDebouncedFilter] = useState(initialFilter)
  const [page, setPage] = useState(1)
  const [showIbtCreateModal, setShowIbtCreateModal] = useState(false)
  const [isCreatingIbt, setIsCreatingIbt] = useState(false)
  const navigate = useNavigate()
  const { data: dispatchTicketsData, isLoading: isLoadingTickets } = useGetDispatchTicketsQuery({
    page,
    pageSize: PAGE_SIZE,
    filter: JSON.stringify(debouncedFilter),
  })

  const { data: carriersData, isLoading: isLoadingCarriersData } = useGetCustomersQuery({
    filter: JSON.stringify({ type: 'CARRIER' }),
  })

  const onIbtCreated = createIbtRes => {
    setIsCreatingIbt(false)
    setShowIbtCreateModal(false)
    onHideModal()
    navigate(`/app/purchases/ticket-library/${createIbtRes.id}`)
  }

  const { handleCreateIbtConfirmed } = useCreateIbtFromDt({
    selectedDispatchTicketId: selectedDtArray[0],
    onIbtCreated,
    inboundTicketData: inboundTicketData,
    materialTypesData,
    formData,
    carriersData,
  })

  const onSubmit = async () => {
    dispatch(setSubmittedFromDispatchBlade(true))
    setIsCreatingIbt(true)

    // let custom hook handle details of creating IBT and updating the dispatch ticket
    handleCreateIbtConfirmed()
  }

  // Debounce search + filter update
  useEffect(() => {
    const debouncer = debounce(() => setDebouncedFilter(filter), 400)
    debouncer()
    return debouncer.cancel
  }, [filter])

  useEffect(function markFormAsClean() {
    onFormStateChange(false)
  }, [])

  const transformedRows = (dispatchTicketsData?.Items || []).slice().map(dt => ({
    id: dt.id,
    ticketId: dt.externalId,
    ticketLink: `/app/load-board/ticket-history/${dt.id}`,
    status: dt.status,
    type: dt.type,
    driver: dt.driver?.name,
    assetNames: getRelevantAssets(dt),
    customer: dt.customer?.displayName,
    location: dt.location?.name,
    scheduled: dateFormatter(dt.scheduleDate),
  }))

  // mui is 0 indexed, api is 1 indexed
  const onPageChange = pageNumber => setPage(pageNumber + 1)

  return (
    <Stack height="100vh">
      {showIbtCreateModal && (
        <AlertModal
          open={showIbtCreateModal}
          modalType="payment"
          title="Link Dispatch Ticket"
          question={`Linking this Dispatch Ticket will override any 
          customer and transportation information you've already entered.  
          Are you sure you want to proceed?`}
          confirmLabel="Link Dispatch Ticket"
          cancelLabel="CANCEL"
          description=""
          onClose={() => {
            setShowIbtCreateModal(false)
          }}
          onSubmit={onSubmit}
          showIsLoading={isCreatingIbt}
        />
      )}
      <FormModalHeading
        title="Select From Dispatch Tickets"
        backButtonPosition="right"
        onBack={() => onHideModal()}
        hideFooter
      />
      <Divider />

      <DispatchTicketFilterBar filters={filter} setFilter={setFilter} inbound={enableIntercompanyDispatch} />

      <Box flexGrow={1}>
        <BasicDataGridV2
          paginationMode="server"
          rowCount={dispatchTicketsData?.totalResultsCount || 0}
          onPageChange={onPageChange}
          pageSize={PAGE_SIZE}
          loading={isLoadingTickets || isLoadingCarriersData}
          pagination
          rows={transformedRows}
          columns={getColumns(selectedDtArray)}
          disableColumnMenu
          selectionModel={selectedDtArray}
          onSelectionModelChange={onSelectionModelChange}
        />
      </Box>
      <FormModalCustomActionBar
        buttonConfigs={getActionButtons(setShowIbtCreateModal, onHideModal, selectedDtArray.length > 0)}
        justify="flex-start"
      />
    </Stack>
  )
}

CreateFromDispatchTicketModal.propTypes = {
  onFormStateChange: PropTypes.func,
  onHideModal: PropTypes.func,
}

CreateFromDispatchTicketModal.defaultProps = {
  onFormStateChange: noop,
  onHideModal: noop,
}

export default CreateFromDispatchTicketModal
